import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import PolicyTab from '../../../components/common/PolicyTab';
import ChartCard from '../../../components/charts/ChartCard';
import { BarChart, DoughnutChart, EmptyChart } from '../../../components/charts/AnalyticsCharts';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const LEAVE_STATUS = {
  proposed:     { bs: 'warning',   label: 'Proposed' },
  approved:     { bs: 'success',   label: 'Approved' },
  rejected:     { bs: 'danger',    label: 'Rejected' },
  cancelled:    { bs: 'secondary', label: 'Cancelled' },
  auto_expired: { bs: 'danger',    label: 'Auto-Expired' },
  auto_rejected:{ bs: 'warning',   label: 'Auto-Rejected' },
};

const SALARY_STATUS = {
  draft:    { bs: 'secondary', label: 'Draft' },
  pending:  { bs: 'warning',  label: 'Pending' },
  approved: { bs: 'info',     label: 'Approved' },
  paid:     { bs: 'success',  label: 'Paid' },
};

const LEAVE_TYPES = [
  { key: 'casual', label: 'Casual', color: '#2196f3' },
  { key: 'sick', label: 'Sick', color: '#4caf50' },
  { key: 'earned', label: 'Earned', color: '#ff9800' },
  { key: 'unpaid', label: 'Unpaid', color: '#9c27b0' },
  { key: 'maternity', label: 'Maternity', color: '#e91e63' },
  { key: 'paternity', label: 'Paternity', color: '#00bcd4' },
  { key: 'work_from_home', label: 'WFH', color: '#607d8b' },
  { key: 'permission', label: 'Permission', color: '#795548' },
];

const fmtCurrency = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

// ─── Leave Approvals Tab ─────────────────────────────────────────────────────

const BLANK_APPLY = {
  leaveType: 'casual', leaveDuration: 'full_day',
  startDate: '', endDate: '', totalDays: 1,
  permissionHours: 1, reason: '', isEmergency: false, emergencyNote: '',
};

function LeaveApprovalsTab({ canApprove }) {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showApply, setShowApply] = useState(false);
  const [applyForm, setApplyForm] = useState(BLANK_APPLY);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const setF = (k, v) => setApplyForm(p => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    const r = await api.get('/hrm/leave', { params }).catch(() => ({ data: { records: [] } }));
    setLeaves(r.data?.records || r.data?.data || []);
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, status, reason) => {
    try {
      if (status === 'approved') {
        await api.patch(`/hrm/leave/${id}/approve`, reason ? { note: reason } : {});
      } else {
        await api.patch(`/hrm/leave/${id}/reject`, { note: reason || '' });
      }
      setToast({ show: true, message: `Leave ${status}.`, type: 'success' });
      setRejectId(null); setRejectReason('');
      load();
    } catch (e) {
      setToast({ show: true, message: e.response?.data?.message || 'Action failed', type: 'danger' });
    }
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!applyForm.startDate) return setToast({ show: true, message: 'Start date is required.', type: 'danger' });
    const payload = { ...applyForm };
    if (payload.leaveDuration === 'hours') {
      payload.endDate = payload.startDate;
    } else if (!payload.endDate) {
      payload.endDate = payload.startDate;
    }
    if (payload.leaveDuration !== 'hours') {
      const days = Math.max(1, Math.ceil((new Date(payload.endDate) - new Date(payload.startDate)) / 86400000) + 1);
      payload.totalDays = ['morning_half_day', 'afternoon_half_day'].includes(payload.leaveDuration) ? 0.5 : days;
    } else {
      payload.totalDays = 0;
    }
    setSubmitting(true);
    try {
      await api.post('/hrm/leave', payload);
      setToast({ show: true, message: 'Leave application submitted successfully!', type: 'success' });
      setApplyForm(BLANK_APPLY);
      setShowApply(false);
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to submit leave.', type: 'danger' });
    }
    setSubmitting(false);
  };

  const colCount = canApprove ? 8 : 7;

  return (
    <div>
      {/* Header row: status filters + apply button */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex gap-2 flex-wrap">
          {['', 'proposed', 'approved', 'rejected', 'cancelled'].map(s => (
            <button key={s} className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setStatusFilter(s)}>
              {s ? LEAVE_STATUS[s]?.label : 'All'}
            </button>
          ))}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowApply(v => !v)}>
          <i className="bi bi-plus-lg me-1"></i>Apply Leave
        </button>
      </div>

      {/* Apply Leave Form */}
      {showApply && (
        <div className="card mb-3 border-primary border-opacity-25">
          <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
            <span className="fw-semibold" style={{ fontSize: 14 }}>
              <i className="bi bi-calendar-plus me-2 text-primary"></i>Apply for Leave
            </span>
            <button className="btn-close btn-sm" onClick={() => { setShowApply(false); setApplyForm(BLANK_APPLY); }}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleApplySubmit}>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label" style={{ fontSize: 12 }}>Leave Type *</label>
                  <select className="form-select form-select-sm" value={applyForm.leaveType}
                    onChange={e => setF('leaveType', e.target.value)}>
                    {LEAVE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={{ fontSize: 12 }}>Duration</label>
                  <select className="form-select form-select-sm" value={applyForm.leaveDuration}
                    onChange={e => setF('leaveDuration', e.target.value)}>
                    <option value="full_day">Full Day</option>
                    <option value="morning_half_day">Morning Half Day</option>
                    <option value="afternoon_half_day">Afternoon Half Day</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label" style={{ fontSize: 12 }}>From *</label>
                  <input type="date" className="form-control form-control-sm" required
                    value={applyForm.startDate}
                    onChange={e => {
                      setF('startDate', e.target.value);
                      if (applyForm.leaveDuration === 'hours') setF('endDate', e.target.value);
                    }} />
                </div>
                {applyForm.leaveDuration !== 'hours' ? (
                  <div className="col-md-2">
                    <label className="form-label" style={{ fontSize: 12 }}>To *</label>
                    <input type="date" className="form-control form-control-sm" required
                      min={applyForm.startDate}
                      value={applyForm.endDate}
                      onChange={e => setF('endDate', e.target.value)} />
                  </div>
                ) : (
                  <div className="col-md-2">
                    <label className="form-label" style={{ fontSize: 12 }}>Hours (max 2)</label>
                    <input type="number" className="form-control form-control-sm" min={0.5} max={2} step={0.5}
                      value={applyForm.permissionHours}
                      onChange={e => setF('permissionHours', parseFloat(e.target.value) || 1)} />
                  </div>
                )}
                <div className="col-md-3">
                  <label className="form-label" style={{ fontSize: 12 }}>Reason *</label>
                  <input type="text" className="form-control form-control-sm" required
                    value={applyForm.reason} placeholder="Brief reason..."
                    onChange={e => setF('reason', e.target.value)} />
                </div>
                <div className="col-12 d-flex align-items-center gap-3">
                  <div className="form-check mb-0">
                    <input className="form-check-input" type="checkbox" id="chkEmergency"
                      checked={applyForm.isEmergency}
                      onChange={e => setF('isEmergency', e.target.checked)} />
                    <label className="form-check-label" htmlFor="chkEmergency" style={{ fontSize: 12 }}>Emergency</label>
                  </div>
                  {applyForm.isEmergency && (
                    <input type="text" className="form-control form-control-sm" style={{ maxWidth: 300 }}
                      placeholder="Emergency note..." value={applyForm.emergencyNote}
                      onChange={e => setF('emergencyNote', e.target.value)} />
                  )}
                  <div className="ms-auto d-flex gap-2">
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => { setShowApply(false); setApplyForm(BLANK_APPLY); }}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                      {submitting && <span className="spinner-border spinner-border-sm me-1"></span>}
                      Submit Application
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th style={{ fontSize: 12 }}>Employee</th>
                <th style={{ fontSize: 12 }}>Type</th>
                <th style={{ fontSize: 12 }}>Duration</th>
                <th style={{ fontSize: 12 }}>From</th>
                <th style={{ fontSize: 12 }}>To</th>
                <th style={{ fontSize: 12 }}>Days / Hrs</th>
                <th style={{ fontSize: 12 }}>Reason</th>
                <th style={{ fontSize: 12 }}>Status</th>
                {canApprove && <th style={{ fontSize: 12 }} className="text-end">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {leaves.map(l => {
                const displayStatus = l.status === 'rejected' && l.rejectionReason && l.rejectionReason.includes('Applied less than 24 hours')
                  ? 'auto_rejected'
                  : l.status;
                const st = LEAVE_STATUS[displayStatus] || LEAVE_STATUS.proposed;
                const lt = LEAVE_TYPES.find(t => t.key === l.leaveType);
                const empName = l.employeeName || l.employee?.email || 'Employee';
                return (
                  <tr key={l._id}>
                    <td style={{ fontSize: 13, fontWeight: 500 }}>
                      {empName}
                      {l.employee?.email && (
                        <div style={{ fontSize: 11, color: '#6c757d' }}>{l.employee.email}</div>
                      )}
                    </td>
                    <td>
                      <span className="badge" style={{ background: `${lt?.color || '#666'}20`, color: lt?.color || '#666', fontSize: 11 }}>
                        {lt?.label || l.leaveType}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>{(l.leaveDuration || '').replace(/_/g, ' ')}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(l.startDate)}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(l.endDate)}</td>
                    <td style={{ fontSize: 13 }}>{l.leaveType === 'permission' ? `${l.permissionHours || 0}h` : (l.totalDays ?? '—')}</td>
                    <td style={{ fontSize: 12, maxWidth: 180 }} className="text-truncate" title={l.reason}>{l.reason || '—'}</td>
                    <td><span className={`badge bg-${st.bs}`} style={{ fontSize: 11 }}>{st.label}</span></td>
                    {canApprove && (
                      <td className="text-end">
                        {l.status === 'proposed' && (
                          <div className="d-flex gap-1 justify-content-end">
                            <button className="btn btn-sm btn-outline-success" style={{ padding: '2px 8px', fontSize: 11 }}
                              onClick={() => handleAction(l._id, 'approved')}>
                              <i className="bi bi-check-lg me-1"></i>Approve
                            </button>
                            <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 8px', fontSize: 11 }}
                              onClick={() => setRejectId(l._id)}>
                              <i className="bi bi-x-lg me-1"></i>Reject
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {leaves.length === 0 && (
                <tr><td colSpan={colCount + 1} className="text-center text-muted py-4">No leave requests found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject Modal */}
      {rejectId && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header"><h6 className="modal-title">Reject Leave</h6>
                <button className="btn-close" onClick={() => { setRejectId(null); setRejectReason(''); }}></button>
              </div>
              <div className="modal-body">
                <label className="form-label" style={{ fontSize: 13 }}>Rejection Reason</label>
                <textarea className="form-control form-control-sm" rows={3} value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)} placeholder="Enter reason..." />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => { setRejectId(null); setRejectReason(''); }}>Cancel</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleAction(rejectId, 'rejected', rejectReason)}>Reject</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}

// ─── Performance Tab ─────────────────────────────────────────────────────────

function PerformanceTab({ canApprove }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [range, setRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [appraisals, setAppraisals] = useState([]);
  const [memos, setMemos] = useState([]);
  const [showMemoForm, setShowMemoForm] = useState(false);
  const [memoForm, setMemoForm] = useState({ employee: '', memoType: 'performance', severity: 'medium', scoreImpact: -2, title: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const loadEmployees = useCallback(async () => {
    const r = await api.get('/shared/tasks/assignable-users').catch(() => ({ data: { data: [] } }));
    const groups = r.data?.grouped || r.data?.data?.grouped || {};
    const flat = Object.values(groups).flat();
    setEmployees(flat);
    if (!selectedEmployee && flat[0]?._id && canApprove) setSelectedEmployee(flat[0]._id);
  }, [canApprove, selectedEmployee]);

  const load = useCallback(async () => {
    setLoading(true);
    const employee = canApprove ? selectedEmployee : '';
    const rangeParams = {
      ...(range.startDate ? { startDate: range.startDate } : {}),
      ...(range.endDate ? { endDate: range.endDate } : {}),
    };
    const params = { ...rangeParams, ...(employee ? { employee } : {}) };
    const [summaryRes, analyticsRes, appraisalRes, memoRes] = await Promise.all([
      api.get('/training/hrm/performance/summary', { params }).catch(() => ({ data: { data: null } })),
      canApprove ? api.get('/training/hrm/performance/analytics', { params: rangeParams }).catch(() => ({ data: { data: null } })) : Promise.resolve({ data: { data: null } }),
      api.get('/training/hrm/performance/appraisals', { params: employee ? { employee } : {} }).catch(() => ({ data: { data: [] } })),
      api.get('/training/hrm/performance/memos', { params: employee ? { employee } : {} }).catch(() => ({ data: { data: [] } })),
    ]);
    setSummary(summaryRes.data?.data || null);
    setAnalytics(analyticsRes.data?.data || null);
    setAppraisals(appraisalRes.data?.data || []);
    setMemos(memoRes.data?.data || []);
    setLoading(false);
  }, [canApprove, range, selectedEmployee]);

  useEffect(() => { if (canApprove) loadEmployees(); }, [canApprove, loadEmployees]);
  useEffect(() => { if (!canApprove || selectedEmployee || !employees.length) load(); }, [load, canApprove, selectedEmployee, employees.length]);

  const saveMemo = async () => {
    setSaving(true);
    try {
      await api.post('/training/hrm/performance/memos', memoForm);
      setToast({ show: true, message: 'Memo feedback saved.', type: 'success' });
      setShowMemoForm(false);
      setMemoForm({ employee: selectedEmployee || '', memoType: 'performance', severity: 'medium', scoreImpact: -2, title: '', description: '' });
      load();
    } catch (e) {
      setToast({ show: true, message: e.response?.data?.message || 'Failed to save memo.', type: 'danger' });
    }
    setSaving(false);
  };

  const generateAppraisal = async () => {
    if (!selectedEmployee && canApprove) return;
    const appraisalStart = range.startDate || (summary?.reviewPeriod?.startDate ? new Date(summary.reviewPeriod.startDate).toISOString().split('T')[0] : '');
    const appraisalEnd = range.endDate || (summary?.reviewPeriod?.endDate ? new Date(summary.reviewPeriod.endDate).toISOString().split('T')[0] : '');
    if (!appraisalStart || !appraisalEnd) {
      setToast({ show: true, message: 'Select a review range before generating appraisal.', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/training/hrm/performance/appraisals', {
        employee: selectedEmployee,
        startDate: appraisalStart,
        endDate: appraisalEnd,
        label: `${fmtDate(appraisalStart)} - ${fmtDate(appraisalEnd)}`,
        periodType: 'custom',
      });
      setToast({ show: true, message: 'Appraisal generated.', type: 'success' });
      load();
    } catch (e) {
      setToast({ show: true, message: e.response?.data?.message || 'Failed to generate appraisal.', type: 'danger' });
    }
    setSaving(false);
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            {canApprove && (
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: 12 }}>Employee</label>
                <select className="form-select form-select-sm" value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}>
                  <option value="">Select employee...</option>
                  {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                </select>
              </div>
            )}
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: 12 }}>Start Date</label>
              <input type="date" className="form-control form-control-sm" value={range.startDate} onChange={e => setRange(r => ({ ...r, startDate: e.target.value }))} />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: 12 }}>End Date</label>
              <input type="date" className="form-control form-control-sm" value={range.endDate} onChange={e => setRange(r => ({ ...r, endDate: e.target.value }))} />
            </div>
            <div className="col-md-2 d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm w-100" onClick={load}><i className="bi bi-arrow-clockwise me-1"></i>Load</button>
            </div>
          </div>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <>
          {canApprove && analytics && (
            <>
              <div className="row g-3 mb-3">
                <div className="col-md-3"><StatCard title="Employees" value={analytics.meta?.totalEmployees || 0} icon="bi-people-fill" color="#0d6efd" /></div>
                <div className="col-md-3"><StatCard title="Average Score" value={Number(analytics.meta?.averageScore || 0).toFixed(1)} icon="bi-bar-chart-line" color="#198754" /></div>
                <div className="col-md-3"><StatCard title="Top Performer" value={analytics.topPerformers?.[0]?.employeeName || '—'} icon="bi-trophy" color="#6f42c1" /></div>
                <div className="col-md-3"><StatCard title="Top Score" value={analytics.topPerformers?.[0] ? Number(analytics.topPerformers[0].totalScore || 0).toFixed(1) : '0.0'} icon="bi-stars" color="#fd7e14" /></div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-lg-8">
                  <ChartCard
                    title="Employee Performance Ranking"
                    subtitle={analytics.meta?.label || 'Selected period'}
                    icon="bi-bar-chart-line-fill"
                    iconColor="#0d6efd"
                  >
                    {analytics.topPerformers?.length ? (
                      <BarChart
                        height={Math.max(280, analytics.topPerformers.length * 36)}
                        horizontal
                        labels={analytics.topPerformers.map((row) => row.employeeName.length > 24 ? `${row.employeeName.slice(0, 23)}...` : row.employeeName)}
                        datasets={[{ label: 'Score', data: analytics.topPerformers.map((row) => Number(row.totalScore || 0)), color: '#0d6efd' }]}
                        themeColor="#0d6efd"
                        tooltipTitle={(index) => analytics.topPerformers[index]?.employeeName || ''}
                      />
                    ) : <EmptyChart label="employee performance" themeColor="#0d6efd" />}
                  </ChartCard>
                </div>
                <div className="col-lg-4">
                  <ChartCard
                    title="Grade Distribution"
                    subtitle="All employee appraisals in range"
                    icon="bi-pie-chart-fill"
                    iconColor="#198754"
                  >
                    {analytics.gradeDistribution?.length ? (
                      <DoughnutChart
                        height={320}
                        slices={analytics.gradeDistribution.map((item, index) => ({
                          ...item,
                          color: ['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'][index % 5],
                        }))}
                        centerLabel={analytics.meta?.totalEmployees || 0}
                        centerSubLabel="Employees"
                        themeColor="#198754"
                      />
                    ) : <EmptyChart label="grade distribution" themeColor="#198754" />}
                  </ChartCard>
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-lg-6">
                  <ChartCard
                    title="Average Score Breakdown"
                    subtitle="Live component contribution across employees"
                    icon="bi-sliders2"
                    iconColor="#6f42c1"
                  >
                    {analytics.scoreBreakdownAverages?.length ? (
                      <BarChart
                        height={320}
                        labels={analytics.scoreBreakdownAverages.map((row) => row.label)}
                        datasets={[{ label: 'Average', data: analytics.scoreBreakdownAverages.map((row) => Number(row.value || 0).toFixed(1)), color: '#6f42c1' }]}
                        themeColor="#6f42c1"
                      />
                    ) : <EmptyChart label="score breakdown" themeColor="#6f42c1" />}
                  </ChartCard>
                </div>
                <div className="col-lg-6">
                  <div className="card h-100">
                    <div className="card-header bg-white fw-semibold">Employee Score Table</div>
                    <div className="table-responsive" style={{ maxHeight: 320 }}>
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ fontSize: 12 }}>Employee</th>
                            <th style={{ fontSize: 12 }}>Role</th>
                            <th style={{ fontSize: 12 }}>Score</th>
                            <th style={{ fontSize: 12 }}>Grade</th>
                            <th style={{ fontSize: 12 }}>Tasks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(analytics.employees || []).map((row) => (
                            <tr key={row.employeeId}>
                              <td style={{ fontSize: 13, fontWeight: 500 }}>{row.employeeName}</td>
                              <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{String(row.roleKey || '').replace(/_/g, ' ') || '—'}</td>
                              <td style={{ fontSize: 13 }}>{Number(row.totalScore || 0).toFixed(1)}</td>
                              <td><span className="badge bg-primary">{row.grade || '—'}</span></td>
                              <td style={{ fontSize: 12 }}>{row.metrics?.assignedTasksCompleted || 0}/{row.metrics?.assignedTasksTotal || 0}</td>
                            </tr>
                          ))}
                          {!analytics.employees?.length && <tr><td colSpan={5} className="text-center text-muted py-4">No employee analytics available.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {summary && (
            <div className="row g-3 mb-3">
              <div className="col-md-3"><StatCard title="Appraisal Score" value={summary.totalScore?.toFixed?.(1) || '0.0'} icon="bi-speedometer2" color="#0d6efd" /></div>
              <div className="col-md-3"><StatCard title="Grade" value={summary.grade || '—'} icon="bi-award" color="#198754" /></div>
              <div className="col-md-3">
                <div className="stat-card h-100">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="stat-label">Assigned Tasks</div>
                      <div className="stat-value">{`${summary.metrics?.assignedTasksCompleted || 0}/${summary.metrics?.assignedTasksTotal || 0}`}</div>
                      <div style={{ fontSize: 12, color: '#6c757d', minHeight: 48 }}>
                        {summary.recentAssignedTasks?.length
                          ? summary.recentAssignedTasks.slice(0, 2).map((task) => (
                              <div key={task._id} className="text-truncate" title={task.title}>{task.title}</div>
                            ))
                          : 'No task titles in this range'}
                      </div>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-2">
                      <div className="stat-icon" style={{ background: '#6f42c115', color: '#6f42c1' }}>
                        <i className="bi bi-check2-square"></i>
                      </div>
                      {canApprove && summary.recentAssignedTasks?.[0]?._id && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          title="Open task"
                          onClick={() => navigate(`/tasks?taskId=${encodeURIComponent(summary.recentAssignedTasks[0]._id)}`)}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3"><StatCard title="Late Days" value={summary.metrics?.lateDays || 0} icon="bi-alarm" color="#fd7e14" /></div>
            </div>
          )}

          {summary && (
            <div className="row g-3 mb-3">
              <div className="col-lg-7">
                <div className="card h-100">
                  <div className="card-header bg-white fw-semibold">Live Appraisal Summary</div>
                  <div className="card-body">
                    <div className="row g-2">
                      {Object.entries(summary.scoreBreakdown || {}).map(([key, value]) => (
                        <div className="col-md-6" key={key}>
                          <div className="border rounded p-2">
                            <div style={{ fontSize: 11, color: '#6c757d', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</div>
                            <div style={{ fontSize: 18, fontWeight: 700 }}>{Number(value || 0).toFixed(1)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3">
                      <div style={{ fontSize: 12, color: '#6c757d' }}>{summary.summary}</div>
                      {summary.strengths?.length > 0 && <div className="mt-2"><strong style={{ fontSize: 12 }}>Strengths:</strong> <span style={{ fontSize: 12 }}>{summary.strengths.join(' | ')}</span></div>}
                      {summary.concerns?.length > 0 && <div className="mt-2"><strong style={{ fontSize: 12 }}>Concerns:</strong> <span style={{ fontSize: 12 }}>{summary.concerns.join(' | ')}</span></div>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-5">
                <div className="card h-100">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Memo / Feedback</span>
                    {canApprove && <button className="btn btn-primary btn-sm" onClick={() => setShowMemoForm(true)}><i className="bi bi-plus-lg me-1"></i>Add</button>}
                  </div>
                  <div className="card-body" style={{ maxHeight: 340, overflowY: 'auto' }}>
                    {memos.map(memo => (
                      <div key={memo._id} className="border rounded p-2 mb-2">
                        <div className="d-flex justify-content-between">
                          <strong style={{ fontSize: 13 }}>{memo.title}</strong>
                          <span className={`badge bg-${memo.memoType === 'appreciation' ? 'success' : 'danger'}`}>{memo.scoreImpact}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#6c757d' }}>{memo.memoType} · {memo.severity} · {fmtDate(memo.issuedDate)}</div>
                        <div style={{ fontSize: 12 }} className="mt-1">{memo.description}</div>
                      </div>
                    ))}
                    {memos.length === 0 && <div className="text-muted text-center py-4">No memo feedback available.</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {canApprove && (
            <div className="d-flex justify-content-end mb-3">
              <button className="btn btn-outline-success btn-sm" onClick={generateAppraisal} disabled={saving || !selectedEmployee}>
                Generate Appraisal
              </button>
            </div>
          )}

          <div className="card">
            <div className="card-header bg-white fw-semibold">Saved Appraisals</div>
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ fontSize: 12 }}>Employee</th>
                    <th style={{ fontSize: 12 }}>Period</th>
                    <th style={{ fontSize: 12 }}>Score</th>
                    <th style={{ fontSize: 12 }}>Grade</th>
                    <th style={{ fontSize: 12 }}>Reviewer</th>
                    <th style={{ fontSize: 12 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {appraisals.map(row => (
                    <tr key={row._id}>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{row.employee?.firstName ? `${row.employee.firstName} ${row.employee.lastName}` : 'Me'}</td>
                      <td style={{ fontSize: 12 }}>{fmtDate(row.reviewPeriod?.startDate)} - {fmtDate(row.reviewPeriod?.endDate)}</td>
                      <td style={{ fontSize: 13 }}>{Number(row.totalScore || 0).toFixed(1)}</td>
                      <td><span className="badge bg-primary">{row.grade || '—'}</span></td>
                      <td style={{ fontSize: 12 }}>{row.reviewer?.firstName ? `${row.reviewer.firstName} ${row.reviewer.lastName}` : '—'}</td>
                      <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{row.status || 'published'}</td>
                    </tr>
                  ))}
                  {appraisals.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">No appraisals generated yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showMemoForm && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">Add Memo / Feedback</h5><button className="btn-close" onClick={() => setShowMemoForm(false)}></button></div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Employee</label>
                    <select className="form-select" value={memoForm.employee} onChange={e => setMemoForm(f => ({ ...f, employee: e.target.value }))}>
                      <option value="">Select employee...</option>
                      {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Type</label>
                    <select className="form-select" value={memoForm.memoType} onChange={e => setMemoForm(f => ({ ...f, memoType: e.target.value }))}>
                      <option value="performance">Performance</option>
                      <option value="discipline">Discipline</option>
                      <option value="warning">Warning</option>
                      <option value="appreciation">Appreciation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Impact</label>
                    <input type="number" className="form-control" value={memoForm.scoreImpact} onChange={e => setMemoForm(f => ({ ...f, scoreImpact: Number(e.target.value) }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Title</label>
                    <input className="form-control" value={memoForm.title} onChange={e => setMemoForm(f => ({ ...f, title: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" rows={4} value={memoForm.description} onChange={e => setMemoForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowMemoForm(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={saveMemo} disabled={saving || !memoForm.employee || !memoForm.title || !memoForm.description}>Save Memo</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}

// ─── Payroll Tab ─────────────────────────────────────────────────────────────

function PayrollTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const monthStr = String(month).padStart(2, '0');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get('/training/hrm/salaries', { params: { year, month: monthStr } }).catch(() => ({ data: { data: [] } }));
    setSalaries(r.data?.data || []);
    setLoading(false);
  }, [year, monthStr]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/training/hrm/payroll/generate', { year, month });
      const d = r.data?.data || {};
      setToast({ show: true, message: `Generated: ${d.generated}, Skipped: ${d.skipped}, Errors: ${d.errors}`, type: 'success' });
      load();
    } catch (e) {
      setToast({ show: true, message: e.response?.data?.message || 'Generation failed', type: 'danger' });
    }
    setGenerating(false);
  };

  const handleAction = async (id, action) => {
    try {
      if (action === 'approve') await api.put(`/training/hrm/salaries/${id}/approve`);
      else if (action === 'pay') await api.put(`/training/hrm/salaries/${id}/pay`, { paymentMethod: 'bank_transfer' });
      setToast({ show: true, message: `Salary ${action}d.`, type: 'success' });
      load();
    } catch (e) {
      setToast({ show: true, message: e.response?.data?.message || 'Action failed', type: 'danger' });
    }
  };

  return (
    <div>
      <div className="d-flex gap-2 mb-3 align-items-end flex-wrap">
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Month</label>
          <select className="form-select form-select-sm" value={month} onChange={e => setMonth(parseInt(e.target.value))}
            style={{ width: 150 }}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Year</label>
          <select className="form-select form-select-sm" value={year} onChange={e => setYear(parseInt(e.target.value))}
            style={{ width: 100 }}>
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleGenerate} disabled={generating}>
          {generating ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-gear me-1"></i>}
          Generate from Attendance
        </button>
      </div>

      <div className="alert alert-info py-2" style={{ fontSize: 12 }}>
        <strong>Payroll Workflow:</strong> Generate → Review (Draft) → Approve → Mark as Paid
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th style={{ fontSize: 12 }}>Employee</th>
                <th style={{ fontSize: 12 }}>Present</th>
                <th style={{ fontSize: 12 }}>Absent</th>
                <th style={{ fontSize: 12 }}>Half Day</th>
                <th style={{ fontSize: 12 }}>On Leave</th>
                <th style={{ fontSize: 12 }}>Gross</th>
                <th style={{ fontSize: 12 }}>Deductions</th>
                <th style={{ fontSize: 12 }}>Net Pay</th>
                <th style={{ fontSize: 12 }}>Status</th>
                <th style={{ fontSize: 12 }} className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {salaries.map(s => {
                const st = SALARY_STATUS[s.status] || SALARY_STATUS.draft;
                return (
                  <React.Fragment key={s._id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === s._id ? null : s._id)}>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>
                        <i className={`bi bi-chevron-${expanded === s._id ? 'down' : 'right'} me-1`} style={{ fontSize: 10 }}></i>
                        {s.employee?.firstName} {s.employee?.lastName}
                      </td>
                      <td style={{ fontSize: 13 }}>{s.presentDays}</td>
                      <td style={{ fontSize: 13 }}>{s.absentDays}</td>
                      <td style={{ fontSize: 13 }}>{s.halfDays}</td>
                      <td style={{ fontSize: 13 }}>{s.leaveDays}</td>
                      <td style={{ fontSize: 13 }}>{fmtCurrency(s.grossSalary)}</td>
                      <td style={{ fontSize: 13, color: '#dc3545' }}>{fmtCurrency(s.deductions?.total)}</td>
                      <td style={{ fontSize: 13, fontWeight: 600 }}>{fmtCurrency(s.netSalary)}</td>
                      <td><span className={`badge bg-${st.bs}`} style={{ fontSize: 10 }}>{st.label}</span></td>
                      <td className="text-end" onClick={e => e.stopPropagation()}>
                        {s.status === 'draft' && (
                          <button className="btn btn-sm btn-outline-success" style={{ padding: '2px 6px', fontSize: 11 }}
                            onClick={() => handleAction(s._id, 'approve')} title="Approve">
                            <i className="bi bi-check-lg"></i>
                          </button>
                        )}
                        {s.status === 'approved' && (
                          <button className="btn btn-sm btn-outline-primary" style={{ padding: '2px 6px', fontSize: 11 }}
                            onClick={() => handleAction(s._id, 'pay')} title="Mark Paid">
                            <i className="bi bi-cash-coin"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === s._id && (
                      <tr>
                        <td colSpan={10} className="bg-light p-3">
                          <div className="row g-3">
                            <div className="col-md-4">
                              <h6 style={{ fontSize: 13 }}>Attendance</h6>
                              {[
                                ['Working Days', s.workingDays], ['Present', s.presentDays],
                                ['Half Days', s.halfDays], ['Absent', s.absentDays], ['On Leave', s.leaveDays],
                              ].map(([l, v]) => (
                                <div key={l} className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                                  <span className="text-muted">{l}</span><span>{v || 0}</span>
                                </div>
                              ))}
                            </div>
                            <div className="col-md-4">
                              <h6 style={{ fontSize: 13 }}>Deductions</h6>
                              {[
                                ['PF', s.deductions?.pf], ['TDS', s.deductions?.tds],
                                ['Absent Days', s.deductions?.absent], ['Half Days', s.deductions?.halfDay],
                                ['Permission', s.deductions?.permission], ['Unpaid Leave', s.deductions?.unpaidLeave],
                                ['Other', s.deductions?.other],
                              ].filter(([,v]) => v).map(([l, v]) => (
                                <div key={l} className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                                  <span className="text-muted">{l}</span><span className="text-danger">{fmtCurrency(v)}</span>
                                </div>
                              ))}
                              <hr className="my-1" />
                              <div className="d-flex justify-content-between" style={{ fontSize: 12, fontWeight: 600 }}>
                                <span>Total Deductions</span><span className="text-danger">{fmtCurrency(s.deductions?.total)}</span>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <h6 style={{ fontSize: 13 }}>Salary Summary</h6>
                              {[
                                ['Basic Salary', s.basicSalary],
                                ['Gross Salary', s.grossSalary],
                                ['Total Deductions', s.deductions?.total],
                              ].map(([l, v]) => (
                                <div key={l} className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                                  <span className="text-muted">{l}</span><span>{fmtCurrency(v)}</span>
                                </div>
                              ))}
                              <hr className="my-1" />
                              <div className="d-flex justify-content-between" style={{ fontSize: 13, fontWeight: 700 }}>
                                <span>Net Pay</span><span className="text-success">{fmtCurrency(s.netSalary)}</span>
                              </div>
                              {s.paymentDate && (
                                <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                                  Paid on {fmtDate(s.paymentDate)} via {s.paymentMethod || '—'}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {salaries.length === 0 && (
                <tr><td colSpan={10} className="text-center text-muted py-4">No salary records for {MONTHS[month-1]} {year}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}

// ─── Leave Balances Tab ──────────────────────────────────────────────────────

function LeaveBalancesTab() {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [editBal, setEditBal] = useState(null);
  const [editForm, setEditForm] = useState({ casual: 12, sick: 10, earned: 0 });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get('/training/hrm/leave-balances', { params: { year } }).catch(() => ({ data: { records: [] } }));
    // paginated() puts rows under `records`; fall back to `data` for legacy handlers.
    setBalances(r.data?.records || r.data?.data || []);
    setLoading(false);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = (bal) => {
    setEditBal(bal);
    setEditForm({
      casual: bal.casual?.total ?? 12,
      sick:   bal.sick?.total ?? 10,
      earned: bal.earned?.total ?? 0,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/training/hrm/leave-balances/${editBal.employee._id}`, {
        year, casual: { total: parseFloat(editForm.casual) },
        sick: { total: parseFloat(editForm.sick) }, earned: { total: parseFloat(editForm.earned) },
      });
      setToast({ show: true, message: 'Leave balance updated.', type: 'success' });
      setEditBal(null);
      load();
    } catch (e) {
      setToast({ show: true, message: e.response?.data?.message || 'Update failed', type: 'danger' });
    }
    setSaving(false);
  };

  return (
    <div>
      <div className="d-flex gap-2 mb-3 align-items-end">
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Year</label>
          <select className="form-select form-select-sm" value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ width: 100 }}>
            {[new Date().getFullYear(), new Date().getFullYear() - 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th style={{ fontSize: 12 }}>Employee</th>
                <th style={{ fontSize: 12 }} className="text-center">Casual (Total / Used / Avail)</th>
                <th style={{ fontSize: 12 }} className="text-center">Sick (Total / Used / Avail)</th>
                <th style={{ fontSize: 12 }} className="text-center">Earned (Total / Used / Avail)</th>
                <th style={{ fontSize: 12 }} className="text-center">Unpaid Used</th>
                <th style={{ fontSize: 12 }} className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {balances.map(b => {
                const avail = (type) => Math.max(0, (b[type]?.total || 0) - (b[type]?.used || 0) - (b[type]?.pending || 0));
                return (
                  <tr key={b._id}>
                    <td style={{ fontSize: 13, fontWeight: 500 }}>{b.employee?.firstName} {b.employee?.lastName}</td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {b.casual?.total || 0} / <span className="text-danger">{b.casual?.used || 0}</span> / <span className="text-success">{avail('casual')}</span>
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {b.sick?.total || 0} / <span className="text-danger">{b.sick?.used || 0}</span> / <span className="text-success">{avail('sick')}</span>
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {b.earned?.total || 0} / <span className="text-danger">{b.earned?.used || 0}</span> / <span className="text-success">{avail('earned')}</span>
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>{b.unpaid?.used || 0}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-primary" style={{ padding: '2px 6px' }}
                        onClick={() => handleEdit(b)} title="Edit Balance">
                        <i className="bi bi-pencil" style={{ fontSize: 12 }}></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {balances.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">No leave balances found for {year}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Balance Modal */}
      {editBal && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header"><h6 className="modal-title">Edit Leave Balance</h6>
                <button className="btn-close" onClick={() => setEditBal(null)}></button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 13 }} className="mb-3">
                  <strong>{editBal.employee?.firstName} {editBal.employee?.lastName}</strong> — {year}
                </p>
                {['casual', 'sick', 'earned'].map(k => (
                  <div className="mb-2" key={k}>
                    <label className="form-label" style={{ fontSize: 12, textTransform: 'capitalize' }}>{k} (days)</label>
                    <input type="number" className="form-control form-control-sm" min="0" step="0.5"
                      value={editForm[k]} onChange={e => setEditForm(f => ({ ...f, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setEditBal(null)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}

// ─── Main HRM Page ───────────────────────────────────────────────────────────

export default function HRMPage() {
  const { user, role: authRole } = useAuth();
  const roleKey = authRole?.key || user?.roleKey || '';
  const isAdminRole = ['admin'].includes(roleKey) || user?.userType === 'super_admin';
  const isManagerRole = roleKey === 'manager';
  const canApproveLeaves = isAdminRole || isManagerRole;

  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState({ proposed: 0, approved: 0, rejected: 0 });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/training/hrm/leaves', { params: { status: 'proposed', limit: 1 } });
        // paginated() returns `total` at the top level, not nested under `pagination`.
        const total = r.data?.total ?? r.data?.pagination?.total ?? 0;
        setStats(s => ({ ...s, proposed: total }));
      } catch (_) {}
    })();
  }, []);

  const tabs = [
    { label: `Leave Approvals${stats.proposed ? ` (${stats.proposed})` : ''}`, icon: 'bi-calendar-check' },
    { label: 'Performance', icon: 'bi-star' },
  ];
  if (canApproveLeaves) {
    tabs.push({ label: 'Payroll', icon: 'bi-cash-stack' });
    tabs.push({ label: 'Leave Balances', icon: 'bi-bar-chart' });
  }
  tabs.push({ label: 'Policy / Manual', icon: 'bi-journal-text' });
  const policyTabIndex = tabs.length - 1;

  return (
    <div>
      <PageHeader title="HRM" icon="bi-people" subtitle="Human Resource Management — Leave, Performance & Payroll" />

      {/* Stats */}
      <div className="row g-3 mb-3">
        <div className="col-sm-4">
          <StatCard title="Pending Leaves" value={stats.proposed} icon="bi-hourglass" color="#fd7e14" />
        </div>
        <div className="col-sm-4">
          <StatCard title="This Month" value="—" icon="bi-calendar3" color="#0d6efd" />
        </div>
        <div className="col-sm-4">
          <StatCard title="Payroll Status" value="—" icon="bi-cash-coin" color="#198754" />
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {tabs.map((t, i) => (
          <li className="nav-item" key={i}>
            <button className={`nav-link ${tab === i ? 'active' : ''}`} style={{ fontSize: 13 }}
              onClick={() => setTab(i)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {tab === 0 && <LeaveApprovalsTab canApprove={canApproveLeaves} />}
      {tab === 1 && <PerformanceTab canApprove={canApproveLeaves} />}
      {tab === 2 && canApproveLeaves && <PayrollTab />}
      {tab === 3 && canApproveLeaves && <LeaveBalancesTab />}
      {tab === policyTabIndex && <PolicyTab moduleKey="hrm" label="HR Policy / Manual" />}
    </div>
  );
}
